// The scene's own lights, as the 27 SH floats the PBR shade renders under.
//
// The shade takes ONE light: a degree-2 SH probe. A directional, point or spot
// light is a delta on the sphere, and its SH projection is exact to the bands we
// keep — so a torch dropped in the scene becomes coefficients here and the
// character responds to it, which is the whole point. Without this the shade
// could only ever read a BAKED probe, so moving a light changed nothing.
//
// The basis and the band factors are written out rather than imported from
// three, because `ogs_shade.wgsl` hardcodes the matching products
// (0.886227 / 1.023328 / 0.858086 = A_l * Y_l) and the two have to be read side
// by side. `tests/unit/sceneLightSh.test.mjs` pins them to each other.

import { collectSceneLights } from "../eyes/eyeSceneLights.ts";
import type { Vector3 } from "../math/index.ts";
import { lightProbeToSH27 } from "../../lib/envSamplers.js";

/** three's SphericalHarmonics3 basis at a unit direction, its own axis order. */
export function shBasisAt(x: number, y: number, z: number, out: Float32Array): Float32Array {
  out[0] = 0.282095;
  out[1] = 0.488603 * y;
  out[2] = 0.488603 * z;
  out[3] = 0.488603 * x;
  out[4] = 1.092548 * x * y;
  out[5] = 1.092548 * y * z;
  out[6] = 0.315392 * (3 * z * z - 1);
  out[7] = 1.092548 * x * z;
  out[8] = 0.546274 * (x * x - y * y);
  return out;
}

/** Band-0 factor in `shGetIrradianceAt`; an ambient term is irradiance already,
 *  so it enters as `colour / this`. */
export const SH_IRRADIANCE_BAND0 = 0.886227;
/** Band-1 factor, for the hemisphere light's single directional term. */
export const SH_IRRADIANCE_BAND1 = 1.023328;

const basisScratch = new Float32Array(9);

/** Does a light-probe contribution carry energy? `gain` is three's `intensity`,
 *  which the PANEL can take to 0 (LiveTunePanel's probe toggle sets exactly
 *  that).
 *
 *  The distinction is load-bearing. Asking whether the probe OBJECT exists
 *  makes a DISABLED probe count as the light in force: the shade uploads its
 *  zero contribution and `nextLight` never falls back to the bundle's own
 *  trained environment, so a PBR branch renders BLACK — and turning the probe
 *  off is exactly what someone does to see the character without it. */
export function probeIsLit(sh27: ArrayLike<number>, gain: number): boolean {
  if (!Number.isFinite(gain) || gain === 0) return false;
  return sh27Energy(sh27) * Math.abs(gain) > LIT_EPSILON;
}

/** Add delta lights — unit directions surface→light, linear RGB irradiance each
 *  (`collectSceneLights`' own output shape). */
export function addDeltaLights(
  sh27: Float32Array, directions: ArrayLike<number>, colors: ArrayLike<number>, count: number,
): Float32Array {
  for (let light = 0; light < count; light++) {
    const o = light * 3;
    shBasisAt(directions[o], directions[o + 1], directions[o + 2], basisScratch);
    for (let band = 0; band < 9; band++) {
      const weight = basisScratch[band];
      sh27[band * 3] += colors[o] * weight;
      sh27[band * 3 + 1] += colors[o + 1] * weight;
      sh27[band * 3 + 2] += colors[o + 2] * weight;
    }
  }
  return sh27;
}

/** Add an ambient light: uniform irradiance, so band 0 only. */
export function addAmbient(sh27: Float32Array, rgb: ArrayLike<number>): Float32Array {
  for (let channel = 0; channel < 3; channel++) {
    sh27[channel] += Number(rgb[channel]) / SH_IRRADIANCE_BAND0;
  }
  return sh27;
}

/** Add a hemisphere light: three mixes ground→sky by `0.5 * dot(n, up) + 0.5`,
 *  which is a constant plus one band-1 term along `up`. */
export function addHemisphere(
  sh27: Float32Array, sky: ArrayLike<number>, ground: ArrayLike<number>, up: ArrayLike<number>,
): Float32Array {
  const [ux, uy, uz] = [Number(up[0]), Number(up[1]), Number(up[2])];
  const length = Math.hypot(ux, uy, uz) || 1;
  for (let channel = 0; channel < 3; channel++) {
    const mean = (Number(sky[channel]) + Number(ground[channel])) / 2;
    const swing = (Number(sky[channel]) - Number(ground[channel])) / 2;
    sh27[channel] += mean / SH_IRRADIANCE_BAND0;
    const k = swing / SH_IRRADIANCE_BAND1 / length;
    // Band 1 is ordered (y, z, x) — three's own.
    sh27[3 + channel] += k * uy;
    sh27[6 + channel] += k * uz;
    sh27[9 + channel] += k * ux;
  }
  return sh27;
}

type Rgb = { r: number; g: number; b: number };

/** Structural slice of the light objects walked — three's own shape, without
 *  importing three into a module a node test has to load. */
interface AmbientCandidate {
  visible?: boolean;
  intensity?: number;
  color?: Rgb;
  groundColor?: Rgb;
  isAmbientLight?: boolean;
  isHemisphereLight?: boolean;
  matrixWorld?: { elements: ArrayLike<number> };
  children?: readonly AmbientCandidate[];
}

/** Ambient and hemisphere lights under `root`, summed into `sh27`. The delta
 *  lights come from `collectSceneLights`, which deliberately skips these — no
 *  lobe can reflect them, but they are most of the DIFFUSE in a lit scene. */
export function addAmbientLights(sh27: Float32Array, root: unknown): number {
  let found = 0;
  const visit = (object: AmbientCandidate | null | undefined): void => {
    if (!object || object.visible === false) return;
    const intensity = Number(object.intensity);
    if (object.color && Number.isFinite(intensity) && intensity > 0) {
      const { color } = object;
      if (object.isAmbientLight) {
        addAmbient(sh27, [color.r * intensity, color.g * intensity, color.b * intensity]);
        found++;
      } else if (object.isHemisphereLight && object.groundColor) {
        const g = object.groundColor;
        const up = object.matrixWorld?.elements
          ? [object.matrixWorld.elements[4], object.matrixWorld.elements[5], object.matrixWorld.elements[6]]
          : [0, 1, 0];
        addHemisphere(sh27,
          [color.r * intensity, color.g * intensity, color.b * intensity],
          [g.r * intensity, g.g * intensity, g.b * intensity], up);
        found++;
      }
    }
    for (const child of object.children ?? []) visit(child);
  };
  visit(root as AmbientCandidate);
  return found;
}

/** Peak |coefficient|. The test for "this light set actually lights something",
 *  which a count cannot answer — a light at intensity 0 is still a light. */
export function sh27Energy(sh27: ArrayLike<number>): number {
  let peak = 0;
  for (let i = 0; i < 27; i++) {
    const magnitude = Math.abs(Number(sh27[i]));
    if (magnitude > peak) peak = magnitude;
  }
  return peak;
}

/** Below this a light set is not lighting anything and the trained environment
 *  is the better answer — a black character reads as a broken decode. */
export const LIT_EPSILON = 1e-6;

/** What `LightProbeVolume` publishes; absent for an environment with no baked grid. */
export interface LightProbeLike {
  sh?: { coefficients?: ArrayLike<{ x: number; y: number; z: number }> };
  /** three multiplies irradiance by this; reading the raw coefficients does not. */
  intensity?: number;
}

/** Three states, not two — "installed but dark" is a different fix from "absent". */
export type ProbeState = "absent" | "lit" | "installed but dark";

export interface CombinedLight {
  /** Analytic lights that carried energy — delta, ambient and hemisphere. */
  lights: number;
  hasScene: boolean;
  hasProbe: boolean;
  probeState: ProbeState;
}

/** The light in force at `at`, summed into `out27`: scene lights plus the probe, world-space.
 *  Notes: ~/comments/aos-threejs-poc/sceneLightSh_combineLight_one_definition.md */
export function combineLight(
  out27: Float32Array, root: unknown, at: Vector3,
  probe: LightProbeLike | null | undefined, probeScratch: Float32Array,
  maxLights = 8,
): CombinedLight {
  out27.fill(0);
  let lights = 0;
  if (root) {
    const set = collectSceneLights(root, at, maxLights);
    addDeltaLights(out27, set.directions, set.colors, set.count);
    lights = set.count + addAmbientLights(out27, root);
  }
  const hasScene = sh27Energy(out27) > LIT_EPSILON;

  let hasProbe = false;
  let probeState: ProbeState = "absent";
  if (probe?.sh?.coefficients) {
    lightProbeToSH27(probe, probeScratch);
    const gain = probe.intensity ?? 1;
    // ENERGY, not presence: a probe the panel switched off is installed at intensity 0.
    hasProbe = probeIsLit(probeScratch, gain);
    probeState = hasProbe ? "lit" : "installed but dark";
    if (hasProbe) for (let i = 0; i < 27; i++) out27[i] += probeScratch[i] * gain;
  }
  return { lights, hasScene, hasProbe, probeState };
}
